import { useTranslation } from 'react-i18next';
import { useMemo, useContext } from 'react';
import { useAtom } from 'jotai';

import useEvents from 'lib/hooks/useEvents';
import TransmissionTap from 'lib/Tracking/Srp/SearchFilters/TransmissionTap';
import ExpandCollapseTap from 'lib/Tracking/Srp/SearchFilters/ExpandCollapseTap';
import { Features } from 'lib/EventSchema';
import { useTransmissionFilter } from 'lib/hooks/useSrpFilters';

import LabeledCheckBox from 'components/Shared/SearchFilter/CheckBox/LabeledCheckBox';
import SrpEventContext from 'contexts/SrpEventContext';
import Accordion from 'components/Shared/SearchFilter/Accordion';
import { formatNumberWithCommas } from 'lib/utils';
import classNames from 'classnames';
import { colors } from 'components/styles/variables';

import { SrpTransmissionName } from 'types/srpFilters';
import { TransmissionFacet } from 'lib/apis/SearchApi/types';
import FilterAccordionAtom, { FilterAccordionName } from 'atoms/FilterAccordionAtom';

interface TransmissionFilterProps {
  transmissionFacets?: TransmissionFacet;
}

const transmissions: SrpTransmissionName[] = ['automatic', 'manual'];

export default function TransmissionFilter({
  transmissionFacets,
}: TransmissionFilterProps): JSX.Element {
  const logger = useEvents();
  const { sourcePage } = useContext(SrpEventContext);
  const { t } = useTranslation();
  const { value, setValue } = useTransmissionFilter();

  const [filterAccordionAtom, setFilterAccordionAtom] = useAtom(FilterAccordionAtom);
  const filterExpanded = filterAccordionAtom[FilterAccordionName.Transmission];

  function handleAccordionClick(): void {
    setFilterAccordionAtom({
      [FilterAccordionName.Transmission]: !filterExpanded,
    });
    logger.log(
      ExpandCollapseTap({
        feature: Features.transmission,
        expanded: !filterExpanded,
        sourcePage,
      })
    );
  }

  const facetCounts = useMemo(() => {
    return transmissions.reduce((acc, type) => {
      const hasZeroFacets =
        transmissionFacets?.[type] === undefined || transmissionFacets?.[type] === 0;
      acc[type] = {
        count: hasZeroFacets ? '0' : formatNumberWithCommas(transmissionFacets?.[type] as number),
        hasZeroFacets,
      };
      return acc;
    }, {} as Record<SrpTransmissionName, { count: string; hasZeroFacets: boolean }>);
  }, [transmissionFacets]);

  function onTap(label: string, isSelected: boolean): void {
    setValue({ ...value, [label]: isSelected });
    logger.log(TransmissionTap(label, sourcePage));
  }

  return (
    <Accordion
      title={t('srp.filters.transmission.header')}
      expanded={filterExpanded}
      onClick={handleAccordionClick}
    >
      <div className="transmission-checkbox-container">
        {transmissions.map((type) => (
          <div key={type} className="transmission-item">
            <LabeledCheckBox
              label={t(`srp.filters.transmission.${type}`)}
              aria-label={`${type}-label`}
              isChecked={value[type] ?? false}
              onClick={(isSelected): void => onTap(type, isSelected)}
              style={{ width: 'auto', paddingRight: '5px' }}
              disabled={facetCounts[type].hasZeroFacets}
            />
            <span className={classNames({ 'zero-facets': facetCounts[type].hasZeroFacets })}>
              {`(${facetCounts[type].count})`}
            </span>
          </div>
        ))}
      </div>
      <style jsx>{`
        .transmission-checkbox-container {
          margin-bottom: 8px;
        }
        .transmission-item {
          display: flex;
          align-items: center;
        }
        .zero-facets {
          color: ${colors.G2};
          cursor: not-allowed;
        }
      `}</style>
    </Accordion>
  );
}
